/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, Tabs, Tab } from '@mui/material';
import ZampSettings from './zamp_settings';
import DateRangePicker from './historical_sync';
import LogsGrid from './Logs_grid';
import { navLogoUrl } from "./brandAssets";
import DisconnectPage from "./disconnect_page";
import Integration from "./Integration";
import ConnectPage from "./ConnectPage";
import ErrorPage from "./ErrorPage";
import SyncQuotes from "./sync_quotes";
import SyncCreditNotes from "./sync_creditnotes";
import TaxCodeMappingUtility from "./taxcode_mapping";
import CustomerEntityMappingUtility from "./customerentity_mapping";
import LoginPage from "./LoginPage";
import AdminDashboard from "./AdminDashboard";
import ProtectedRoute from "./ProtectedRoute";
import { useAuth } from "./AuthContext";
import ChangePassword from "./ChangePassword";

const NO_NAV_PATHS = ['/login', '/', '/dashboard'];

function App() {
    const location = useLocation();
    const navigate = useNavigate();

    const hideTabsOnPaths = ['/disconnect', '/launch', '/error'];
    const shouldHideTabs = hideTabsOnPaths.some(path => location.pathname.startsWith(path));
    const hideNavBar = NO_NAV_PATHS.some(path => location.pathname === path);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const userIdFromUrl = queryParams.get("userid");

        if (userIdFromUrl) {
            sessionStorage.setItem("rilletUserId", userIdFromUrl);
            window.dispatchEvent(new Event("RilletUserIdChange"));
        } else if (!sessionStorage.getItem("rilletUserId")) {
            navigate('/login');
        }
    }, [location.search, navigate]);

    return (
        <>
            {!hideNavBar && <NavBar />}
            <Box mt={hideNavBar ? 0 : 8}>
                {!hideNavBar && !shouldHideTabs && <TabsSection />}
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/dashboard" element={
                        <ProtectedRoute adminOnly>
                            <AdminDashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/settings/:rilletUserId?" element={
                        <ProtectedRoute><ZampSettings /></ProtectedRoute>
                    } />
                    <Route path="/services/:rilletUserId?" element={
                        <ProtectedRoute><DateRangePicker /></ProtectedRoute>
                    } />
                    <Route path="/logs/:rilletUserId?" element={
                        <ProtectedRoute><LogsGrid /></ProtectedRoute>
                    } />
                    <Route path="/integration/:rilletUserId?" element={
                        <ProtectedRoute><Integration /></ProtectedRoute>
                    } />
                    <Route path="/disconnect" element={<DisconnectPage />} />
                    <Route path="/launch" element={<ConnectPage />} />
                    <Route path="/error" element={<ErrorPage />} />
                    <Route path="/quotes/:rilletUserId?" element={
                        <ProtectedRoute><SyncQuotes /></ProtectedRoute>
                    } />
                    <Route path="/creditnotes/:rilletUserId?" element={
                        <ProtectedRoute><SyncCreditNotes /></ProtectedRoute>
                    } />
                    <Route path="/taxcodemapping/:rilletUserId?" element={
                        <ProtectedRoute><TaxCodeMappingUtility /></ProtectedRoute>
                    } />
                    <Route path="/customerentitymapping/:rilletUserId?" element={
                        <ProtectedRoute><CustomerEntityMappingUtility /></ProtectedRoute>
                    } />
                    <Route path="/change-password" element={
                        <ProtectedRoute><ChangePassword /></ProtectedRoute>
                    } />
                </Routes>
            </Box>
        </>
    );
}

function NavBar() {
    const navigate = useNavigate();
    const [rilletUserId, setRilletUserId] = useState(sessionStorage.getItem("rilletUserId"));
    const contactUsUrl = process.env.REACT_APP_CONTACT_US_URL;
    const { logout } = useAuth();

    useEffect(() => {
        const handleRilletUserIdChange = () => setRilletUserId(sessionStorage.getItem("rilletUserId"));
        window.addEventListener("RilletUserIdChange", handleRilletUserIdChange);
        return () => window.removeEventListener("RilletUserIdChange", handleRilletUserIdChange);
    }, []);

    const handleContactUsClick = () => window.open(contactUsUrl, "_blank");
    const handleLogoutClick = () => {
        logout();
        navigate('/login');
    };

    return (
        <AppBar position="fixed" color="primary">
            <Toolbar>
                <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                    <img src={navLogoUrl} alt="Zamp" style={{ height: 22, marginRight: 8, marginTop: 5 }} />
                </Box>
                <Button color="inherit" onClick={() => navigate('/change-password')} disabled={!rilletUserId}>
                    Change Password
                </Button>
                <Button color="inherit" onClick={handleContactUsClick}>
                    Contact Us
                </Button>
                <Button color="inherit" onClick={handleLogoutClick} disabled={!rilletUserId}>
                    Logout
                </Button>
            </Toolbar>
        </AppBar>
    );
}

function TabsSection() {
    const location = useLocation();
    const navigate = useNavigate();
    const [rilletUserId, setRilletUserId] = useState(sessionStorage.getItem("rilletUserId"));
    const [value, setValue] = useState(0);

    useEffect(() => {
        const handleRilletUserIdChange = () => setRilletUserId(sessionStorage.getItem("rilletUserId"));
        window.addEventListener("RilletUserIdChange", handleRilletUserIdChange);
        return () => window.removeEventListener("RilletUserIdChange", handleRilletUserIdChange);
    }, []);

    useEffect(() => {
        if (location.pathname.startsWith("/settings")) setValue(0);
        else if (location.pathname.startsWith("/services")) setValue(1);
        else if (location.pathname.startsWith("/taxcodemapping")) setValue(2);
        else if (location.pathname.startsWith("/customerentitymapping")) setValue(3);
        else if (location.pathname.startsWith("/logs")) setValue(4);
    }, [location.pathname]);

    const handleChange = (event, newValue) => {
        setValue(newValue);

        switch (newValue) {
            case 0:
                navigate(`/settings/${rilletUserId}`);
                break;
            case 1:
                navigate(`/services/${rilletUserId}?mode=full`);
                break;
            case 2:
                navigate(`/taxcodemapping/${rilletUserId}`);
                break;
            case 3:
                navigate(`/customerentitymapping/${rilletUserId}`);
                break;
            case 4:
                navigate(`/logs/${rilletUserId}`);
                break;
            default:
                break;
        }
    };

    return (
        <Box className="zamp-nav-tabs" sx={{ width: '100%', padding: 2 }}>
            <Tabs
                value={value}
                onChange={handleChange}
                textColor="inherit"
                indicatorColor="secondary"
                centered
                sx={{ borderBottom: 1, borderColor: "divider" }}
            >
                <Tab label="Zamp Tax Settings" disabled={!rilletUserId} />
                <Tab label="Historical Data Sync" disabled={!rilletUserId} />
                <Tab label="Tax Code Mapping" disabled={!rilletUserId} />
                <Tab label="Customer Entity Mapping" disabled={!rilletUserId} />
                <Tab label="Logs" disabled={!rilletUserId} />
            </Tabs>
        </Box>
    );
}

export default App;
